import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useId, useRef } from 'react';
import { faArrowUpRight } from '@fortawesome/pro-light-svg-icons/faArrowUpRight';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';
import { useRenamePageMutation } from '../../hooks/useRenamePageMutation/useRenamePageMutation';
import { useScrollToHighlightedPage } from '../../hooks/useScrollToHighlightedPage';
import { DragHandle } from '../dragHandle';
import { Name } from '../name';
import { DataHubService } from 'src/core/services/datahub';
import { ClickContentPageEditSchema } from 'src/core/services/datahub/schemas/moduleList/clickContentPageEditSchema';
import { ClickContentPageNameEditSchema } from 'src/core/services/datahub/schemas/moduleList/clickContentPageNameEditSchema';
import { Actions } from 'src/modules/contentList/components/pageCard/actions';
import { PublishStatus } from 'src/modules/contentList/components/pageCard/publishStatus';
import { Stats } from 'src/modules/contentList/components/pageCard/stats';
import { PageTypeIcon } from 'src/modules/contentList/components/pageTypeIcon/pageTypeIcon';
import { PAGE_DRAG_NODE_PROPS, PAGE_DRAG_HANDLE_PROPS } from 'src/modules/contentList/hooks/usePageDragAndDrop/usePageDragAndDrop';
import { useSelectPage } from 'src/modules/contentList/hooks/useSelectPage';
import { useWebinarPanelUrl } from 'src/modules/contentList/hooks/useWebinarPanelUrl';
import { Button } from 'src/shared/components/button';
import { Checkbox } from 'src/shared/components/form/checkbox';
import { Icon } from 'src/shared/components/icon';
import { Routes } from 'src/shared/constants/routes';
import { HtmlHelper } from 'src/shared/helpers/html';
export function PageCard({ page, isDragging = false }) {
    const checkboxId = useId();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const ref = useRef(null);
    const webinarPanelUrl = useWebinarPanelUrl(page);
    const renamePageMutation = useRenamePageMutation(page);
    const { isSelected, onSelect } = useSelectPage(page.moduleId, page.id);
    const isHighlighted = useScrollToHighlightedPage({
        ref,
        pageId: page.id
    });
    function handleCardClick(event) {
        if (!HtmlHelper.isInteractive(event.target)) {
            navigate({
                pathname: generatePath(Routes.ManagePage, {
                    moduleId: page.moduleId,
                    pageType: page.type,
                    pageId: page.id
                }),
                search: location.search
            });
            DataHubService.sendEvent({
                schema: new ClickContentPageEditSchema({
                    pageType: page.type,
                    componentName: 'CONTENT_CARD'
                })
            });
        }
    }
    function handleEditNameMode() {
        DataHubService.sendEvent({
            schema: new ClickContentPageNameEditSchema({ pageType: page.type })
        });
    }
    return (_jsxs("li", { ref: ref, "aria-labelledby": `page-heading-${page.id}`, className: clsx('z-1 relative grid cursor-pointer grid-rows-[1fr_auto] gap-3 rounded-large border-small bg-neutral-0 py-4 pl-3 pr-4 md:grid-cols-[auto_auto_auto_1fr_auto_auto] md:items-center md:gap-0 [&_*]:md:row-span-full', {
            'border-neutral-200': !isDragging,
            'border-primary-100 opacity-40': isDragging,
            'outline outline-2 outline-primary-300': isHighlighted || isSelected
        }), id: `page-${page.id}`, onClick: handleCardClick, ...PAGE_DRAG_NODE_PROPS, children: [_jsxs("div", { className: "grid min-w-0 grid-cols-[auto_auto_auto_1fr_auto] items-center p-0 md:contents", children: [_jsx(DragHandle, { "aria-label": t('content_list.page_card.drag', { name: page.name }), ...PAGE_DRAG_HANDLE_PROPS }), _jsx(Checkbox, { id: checkboxId, "aria-labelledby": `page-heading-${page.id}`, labelClassName: "mr-3 ml-1 md:ml-2 md:mr-5", onChange: onSelect, checked: isSelected }), _jsx(PageTypeIcon, { pageType: page.type, className: "h-[1.75rem] w-[1.75rem] rounded-large md:mr-2 md:h-[2.75rem] md:w-[2.75rem]" }), _jsx(Name, { defaultValue: page.name, mutation: renamePageMutation, id: `page-heading-${page.id}`, className: "md:!col-[4] md:!row-[1]", onEnterEditMode: handleEditNameMode }), _jsx(Actions, { page: page })] }), page.type !== 'webinar' && (_jsxs("div", { className: "flex flex-wrap items-center gap-3 md:contents", children: [_jsx(PublishStatus, { published: page.published }), _jsx(Stats, { page: page })] })), page.type === 'webinar' && (_jsx("div", { className: "md:contents", children: _jsx(Button, { href: webinarPanelUrl, target: "_blank", size: "small", variation: "tertiary", icon: _jsx(Icon, { icon: faArrowUpRight }), className: "col-[4] !row-[2] w-fit [--hc-button-width:auto]", children: t('content_list.page_card.live_settings') }) }))] }));
}
